import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { reportApi, memberApi, departmentApi, lcClassApi } from '@/api/b2b'
import { announcementApi } from '@/api/announcements'
import { Users, Building2, BookMarked, BookOpen, GraduationCap, Pin } from 'lucide-react'

export default function AdminDashboard() {
  const { t, i18n } = useTranslation('dashboard')
  const { data: classProgressData } = useQuery({
    queryKey: ['classProgressSummary'],
    queryFn: () => reportApi.getClassProgressSummary(),
  })

  const { data: membersData } = useQuery({
    queryKey: ['members'],
    queryFn: () => memberApi.getAll(),
  })

  const { data: departmentsData } = useQuery({
    queryKey: ['departments'],
    queryFn: () => departmentApi.getAll(),
  })

  const { data: classesData } = useQuery({
    queryKey: ['classes'],
    queryFn: () => lcClassApi.getAll(),
  })

  const { data: classDetailsData } = useQuery({
    queryKey: ['dashboardClassDetails'],
    queryFn: () => reportApi.getDashboardClassDetails(),
  })

  const { data: announcementsData } = useQuery({
    queryKey: ['dashboardAnnouncements'],
    queryFn: () => announcementApi.getList({ page: 0, size: 5 }),
  })

  const classProgress = classProgressData?.data?.data
  const members = membersData?.data?.data || []
  const departments = departmentsData?.data?.data || []
  const classes = classesData?.data?.data || []
  const classDetails = classDetailsData?.data?.data || []
  const announcements = announcementsData?.announcements || []

  const stats = [
    {
      label: t('admin.totalMembers'),
      value: members.length,
      icon: Users,
      color: 'bg-blue-500',
    },
    {
      label: t('admin.departments'),
      value: departments.length,
      icon: Building2,
      color: 'bg-green-500',
    },
    {
      label: t('admin.activeClasses'),
      value: classes.length,
      icon: BookMarked,
      color: 'bg-purple-500',
    },
    {
      label: t('admin.classAssignedMembers'),
      value: classProgress?.totalClassMembers || 0,
      subLabel: t('admin.includingDuplicates'),
      icon: Users,
      color: 'bg-indigo-500',
    },
  ]

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('admin.title')}</h1>
        <p className="mt-1 text-gray-500">{t('admin.subtitle')}</p>
      </div>

      {/* Stats Grid - 4 cards */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => {
          const Icon = stat.icon
          return (
            <div key={stat.label} className="p-6 bg-white rounded-xl shadow-sm">
              <div className="flex justify-between items-center mb-2">
                <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                <div className={`w-12 h-12 ${stat.color} rounded-lg flex items-center justify-center`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
              </div>
              <p className="text-sm text-gray-500">
                {stat.label}
                {stat.subLabel && <span className="ml-1 text-xs">{stat.subLabel}</span>}
              </p>
            </div>
          )
        })}
      </div>

      {/* 학습 진행 현황 */}
      {classProgress && (
        <div className="p-6 bg-white rounded-xl shadow-sm">
          <h2 className="mb-4 text-lg font-semibold text-gray-900">{t('admin.learningProgressTitle')} <span className="text-sm font-normal text-gray-400">{t('admin.classBased')}</span></h2>

          <div className="grid grid-cols-3 gap-8">
            <div className="text-center">
              <p className="text-4xl font-bold text-gray-400">{classProgress.notStartedCount}</p>
              <p className="mt-1 text-sm text-gray-500">{t('admin.notStarted')}</p>
            </div>
            <div className="text-center">
              <p className="text-4xl font-bold text-blue-600">{classProgress.inProgressCount}</p>
              <p className="mt-1 text-sm text-gray-500">{t('admin.inProgress')}</p>
            </div>
            <div className="text-center">
              <p className="text-4xl font-bold text-green-600">{classProgress.completedCount}</p>
              <p className="mt-1 text-sm text-gray-500">{t('admin.completed')}</p>
            </div>
          </div>

          {/* Progress Bars */}
          <div className="mt-6 space-y-3">
            <div>
              <div className="flex justify-between mb-1 text-sm text-gray-500">
                <span>{t('admin.overallCompletionRate')}</span>
                <span>{classProgress.completionRate.toFixed(1)}%</span>
              </div>
              <div className="overflow-hidden h-3 bg-gray-200 rounded-full">
                <div
                  className="h-full bg-green-500 rounded-full transition-all duration-500"
                  style={{ width: `${Math.min(classProgress.completionRate, 100)}%` }}
                />
              </div>
            </div>
            <div>
              <div className="flex justify-between mb-1 text-sm text-gray-500">
                <span>{t('admin.averageProgressRate')}</span>
                <span>{classProgress.averageProgressRate.toFixed(1)}%</span>
              </div>
              <div className="overflow-hidden h-3 bg-gray-200 rounded-full">
                <div
                  className="h-full bg-blue-500 rounded-full transition-all duration-500"
                  style={{ width: `${Math.min(classProgress.averageProgressRate, 100)}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 클래스 학습 현황 */}
      {classDetails.length > 0 && (
        <div className="p-6 bg-white rounded-xl shadow-sm">
          <h2 className="mb-4 text-lg font-semibold text-gray-900">{t('admin.classLearningStatus')}</h2>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {classDetails.map((detail) => (
              <div key={detail.classId} className="p-5 rounded-lg border border-gray-200">
                <div className="flex gap-2 items-center mb-4">
                  <GraduationCap className="w-5 h-5 text-purple-600" />
                  <h3 className="font-semibold text-gray-900">{detail.className}</h3>
                </div>

                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div className="flex items-center gap-1.5">
                    <BookOpen className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-500">{t('admin.courses')}</span>
                    <span className="font-medium text-gray-900">{t('admin.courseCount', { count: detail.courseCount })}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">{t('admin.learners')}</span>
                    <span className="ml-1 font-medium text-gray-900">{t('admin.learnerCount', { count: detail.learnerCount })}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">{t('admin.notStarted')}</span>
                    <span className="ml-1 font-medium text-gray-900">{t('admin.notStartedCount', { count: detail.notStartedCount })}</span>
                  </div>
                </div>

                <div className="mt-3 space-y-2">
                  <div>
                    <div className="flex justify-between mb-1 text-sm">
                      <span className="text-gray-500">{t('admin.progressRate')}</span>
                      <span className="font-medium text-blue-600">{detail.progressRate.toFixed(1)}%</span>
                    </div>
                    <div className="overflow-hidden h-2 bg-gray-200 rounded-full">
                      <div
                        className="h-full bg-blue-500 rounded-full transition-all duration-500"
                        style={{ width: `${Math.min(detail.progressRate, 100)}%` }}
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 pt-3 mt-3 text-sm border-t border-gray-100">
                  <div>
                    <span className="text-gray-500">{t('admin.completedLearners')}</span>
                    <span className="ml-1 font-medium text-green-600">{t('admin.completedCount', { count: detail.completedCount })}</span>
                    <span className="ml-1 text-gray-400">({detail.completionRate.toFixed(1)}%)</span>
                  </div>
                  <div>
                    <span className="text-gray-500">{t('admin.moduleQuizAccuracy')}</span>
                    <span className="ml-1 font-medium text-orange-600">
                      {detail.moduleQuizAccuracy != null ? `${detail.moduleQuizAccuracy.toFixed(1)}%` : '-'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">{t('admin.aiqGrowthRate')}</span>
                    <span className="ml-1 font-medium text-purple-600">
                      {detail.aiqGrowthRate != null ? `${detail.aiqGrowthRate.toFixed(1)}%` : '-'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 공지사항 */}
      <div className="p-6 bg-white rounded-xl shadow-sm">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900">{t('admin.announcements')}</h2>
          <Link to="/admin/announcements" className="text-sm text-blue-600 hover:text-blue-700">
            {t('admin.viewAll')} &rarr;
          </Link>
        </div>

        {announcements.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-xs font-medium text-left text-gray-500 uppercase">{t('admin.type')}</th>
                  <th className="px-4 py-3 text-xs font-medium text-left text-gray-500 uppercase">{t('admin.announcementTitle')}</th>
                  <th className="px-4 py-3 text-xs font-medium text-left text-gray-500 uppercase">{t('admin.createdDate')}</th>
                  <th className="px-4 py-3 text-xs font-medium text-left text-gray-500 uppercase">{t('admin.updatedDate')}</th>
                  <th className="px-4 py-3 text-xs font-medium text-left text-gray-500 uppercase">{t('admin.viewCount')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {announcements.map((announcement) => (
                  <tr key={announcement.announcementId} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">
                      {announcement.isPinned ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-700">
                          <Pin className="w-3 h-3" />
                          {t('admin.pinned')}
                        </span>
                      ) : (
                        <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                          {t('admin.general')}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      <Link to={`/admin/announcements/${announcement.announcementId}`} className="hover:text-blue-600">
                        {announcement.title}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {new Date(announcement.createdAt).toLocaleDateString(i18n.language === 'ko' ? 'ko-KR' : 'en-US')}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {announcement.updatedAt
                        ? new Date(announcement.updatedAt).toLocaleDateString(i18n.language === 'ko' ? 'ko-KR' : 'en-US')
                        : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">{announcement.viewCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="py-8 text-center text-gray-400">{t('admin.noAnnouncements')}</p>
        )}
      </div>
    </div>
  )
}
